import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CreditCard,
  Calendar,
  Package,
  AlertTriangle,
  ExternalLink,
  Clock,
  CheckCircle2,
  XCircle,
  ArrowRight,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Skeleton } from '@/app/components/ui/skeleton';
import { Button } from '@/app/components/ui/button';
import { toast } from '@/app/components/ui/simple-toaster';
import { useSubscription, useCreatePortalSession, useStartTrial } from '@/hooks/use-billing';
import { usePermissions } from '@/hooks/use-permissions';
import { formatDateOnly } from '@/lib/date-utils';
import type { SubscriptionStatus } from '@/lib/types';

const STATUS_CONFIG: Record<SubscriptionStatus, { label: string; color: string; icon: typeof CheckCircle2 }> = {
  trialing: { label: 'Free Trial', color: 'bg-blue-100 text-blue-800', icon: Clock },
  active: { label: 'Active', color: 'bg-green-100 text-green-800', icon: CheckCircle2 },
  past_due: { label: 'Past Due', color: 'bg-yellow-100 text-yellow-800', icon: AlertTriangle },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-800', icon: XCircle },
  expired: { label: 'Expired', color: 'bg-gray-100 text-gray-800', icon: XCircle },
};

const MODULE_NAMES: Record<string, string> = {
  inventory: 'H3Gear (Inventory)',
  safety: 'H3Safe (Safety)',
  crew: 'H3Crew (Workforce)',
  projects: 'H3Books (Projects)',
  work: 'H3Project (Work)',
  payroll: 'H3Pay (Payroll)',
  compliance: 'H3Comply (Compliance)',
};

export function SubscriptionPage() {
  const { canManageOrganization } = usePermissions();
  const { data, isLoading, error } = useSubscription();
  const portalSession = useCreatePortalSession();
  const startTrial = useStartTrial();
  const [isStartingTrial, setIsStartingTrial] = useState(false);

  const subscription = data?.subscription;

  const handleManageBilling = async () => {
    if (!subscription?.stripe_customer_id) {
      toast.error('Error', { description: 'No billing account linked. Please subscribe to a plan first.' });
      return;
    }
    try {
      const result = await portalSession.mutateAsync({
        return_url: window.location.href,
      });
      window.location.href = result.portal_url;
    } catch {
      toast.error('Error', { description: 'Could not open billing portal. Stripe may not be configured.' });
    }
  };

  const handleStartTrial = async () => {
    setIsStartingTrial(true);
    try {
      await startTrial.mutateAsync();
      toast.success('Trial Started', {
        description: 'Your 30-day free trial of H3 Complete has begun! All modules are now enabled.',
      });
    } catch {
      toast.error('Error', { description: 'Could not start trial. You may already have an active subscription.' });
    } finally {
      setIsStartingTrial(false);
    }
  };

  if (isLoading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 space-y-6">
        <Skeleton className="h-8 w-1/3" />
        <Skeleton className="h-48" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <p className="text-red-800">Failed to load subscription details. Please try refreshing.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // No subscription — show CTA
  if (!subscription) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 space-y-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Subscription</h1>
          <p className="text-gray-600 mt-1">Manage your H3ight subscription</p>
        </div>

        <Card>
          <CardContent className="pt-8 pb-8 text-center space-y-4">
            <Package className="h-12 w-12 text-gray-400 mx-auto" />
            <h2 className="text-xl font-semibold text-gray-900">No Active Subscription</h2>
            <p className="text-gray-600 max-w-md mx-auto">
              Subscribe to unlock additional H3ight modules like H3Safe, H3Crew, H3Books, H3Project, and H3Pay.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
              {canManageOrganization && (
                <Button onClick={handleStartTrial} disabled={isStartingTrial} size="lg">
                  {isStartingTrial ? 'Starting Trial...' : 'Start 30-Day Free Trial'}
                </Button>
              )}
              <Button variant="outline" size="lg" asChild>
                <Link to="/settings/subscription/plans">
                  View Plans <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const statusConfig = STATUS_CONFIG[subscription.status];
  const StatusIcon = statusConfig.icon;

  const isFlexibleBundle =
    subscription.plan_detail.is_bundle &&
    (subscription.plan_detail.modules_included || []).length === 0;
  const addonsToShow = isFlexibleBundle
    ? subscription.addons.filter((addon) => addon.module_key !== 'inventory')
    : subscription.addons;

  const isPaidTrial = subscription.status === 'trialing' && !!subscription.stripe_subscription_id;
  const cancelScheduled = subscription.cancel_at_period_end || !!subscription.cancel_at;
  const subscriptionEndsDate = isPaidTrial
    ? (subscription.trial_end ?? subscription.cancel_at ?? subscription.current_period_end)
    : (subscription.cancel_at ?? subscription.current_period_end);

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Subscription</h1>
          <p className="text-gray-600 mt-1">Manage your H3ight subscription</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" asChild>
            <Link to="/settings/subscription/plans">Change Plan</Link>
          </Button>
          {canManageOrganization && subscription.stripe_customer_id && (
            <Button onClick={handleManageBilling} disabled={portalSession.isPending}>
              <CreditCard className="h-4 w-4 mr-2" />
              {portalSession.isPending ? 'Opening...' : 'Manage Billing'}
            </Button>
          )}
        </div>
      </div>

      {/* Trial Banner — unpaid trial */}
      {subscription.status === 'trialing' &&
        !subscription.stripe_subscription_id &&
        subscription.trial_days_remaining > 0 && (
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <Clock className="h-5 w-5 text-blue-600" />
                <div>
                  <p className="font-medium text-blue-900">
                    Free Trial — {subscription.trial_days_remaining} day{subscription.trial_days_remaining !== 1 ? 's' : ''} remaining
                  </p>
                  <p className="text-sm text-blue-700">
                    You have access to all modules. Subscribe before your trial ends to keep them.
                  </p>
                </div>
              </div>
              <Button size="sm" asChild>
                <Link to="/settings/subscription/plans">Subscribe Now</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Trial Banner — payment already on file */}
      {isPaidTrial && !cancelScheduled && (
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <Clock className="h-5 w-5 text-blue-600" />
              <p className="font-medium text-blue-900">
                {subscription.trial_end
                  ? `Trial · payment on file · first charge ${formatDateOnly(subscription.trial_end)}`
                  : 'Trial · payment on file'}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Past Due / Grace Period Banner */}
      {subscription.status === 'past_due' && (
        <Card className="border-yellow-200 bg-yellow-50">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 text-yellow-600" />
              <div>
                <p className="font-medium text-yellow-900">Payment Past Due</p>
                <p className="text-sm text-yellow-700">
                  Your payment failed. Please update your billing information to avoid losing access.
                  {subscription.in_grace_period && ' You have a 7-day grace period before modules are disabled.'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Cancelled Banner */}
      {(subscription.cancel_at_period_end || subscription.cancel_at) && subscription.status === 'active' && (
        <Card className="border-orange-200 bg-orange-50">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 text-orange-600" />
              <div>
                <p className="font-medium text-orange-900">Cancellation Scheduled</p>
                <p className="text-sm text-orange-700">
                  Your subscription will end on {(subscription.cancel_at ?? subscription.current_period_end)
                    ? formatDateOnly(subscription.cancel_at ?? subscription.current_period_end)
                    : 'the end of the current period'}.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Paid-trial Cancellation Banner */}
      {isPaidTrial && cancelScheduled && (
        <Card className="border-orange-200 bg-orange-50">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 text-orange-600" />
              <p className="text-sm text-orange-700">
                <span className="font-medium text-orange-900">Cancellation scheduled.</span>
                {subscription.trial_end
                  ? ` Your trial ends ${formatDateOnly(subscription.trial_end)} and you won't be charged.`
                  : " You won't be charged."}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Current Plan Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            Current Plan
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h3 className="text-xl font-bold text-gray-900">{subscription.plan_detail.name}</h3>
              <p className="text-gray-600 text-sm">{subscription.plan_detail.description}</p>
            </div>
            <div className="flex items-center gap-2">
              <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${statusConfig.color}`}>
                <StatusIcon className="h-3.5 w-3.5" />
                {statusConfig.label}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
            <div className="bg-gray-50 dark:bg-secondary rounded-lg p-4">
              <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
                <CreditCard className="h-4 w-4" />
                Billing
              </div>
              <p className="font-semibold text-gray-900">
                ${subscription.billing_cycle === 'annual'
                  ? subscription.plan_detail.price_annual
                  : subscription.plan_detail.price_monthly}
                /{subscription.billing_cycle === 'annual' ? 'year' : 'month'}
              </p>
            </div>
            <div className="bg-gray-50 dark:bg-secondary rounded-lg p-4">
              <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
                <Calendar className="h-4 w-4" />
                Current Period
              </div>
              <p className="font-semibold text-gray-900">
                {subscription.current_period_start
                  ? formatDateOnly(subscription.current_period_start)
                  : '—'}{' '}
                to{' '}
                {subscription.current_period_end
                  ? formatDateOnly(subscription.current_period_end)
                  : '—'}
              </p>
            </div>
            <div className="bg-gray-50 dark:bg-secondary rounded-lg p-4">
              <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
                <Calendar className="h-4 w-4" />
                {cancelScheduled ? 'Subscription ends' : (subscription.status === 'trialing' && !subscription.stripe_subscription_id ? 'Trial Ends' : 'Next Payment')}
              </div>
              <p className="font-semibold text-gray-900">
                {cancelScheduled
                  ? (subscriptionEndsDate ? formatDateOnly(subscriptionEndsDate) : '—')
                  : subscription.status === 'trialing' && subscription.trial_end
                    ? formatDateOnly(subscription.trial_end)
                    : subscription.current_period_end
                      ? formatDateOnly(subscription.current_period_end)
                      : '—'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Included Modules */}
      <Card>
        <CardHeader>
          <CardTitle>Included Modules</CardTitle>
          <CardDescription>Modules enabled by your current plan</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {/* For bundle plans with empty modules_included, always show H3Gear */}
            {isFlexibleBundle && (
              <div className="flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-200 rounded-lg">
                <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0" />
                <span className="text-sm font-medium text-green-800">
                  {MODULE_NAMES['inventory']} (Included)
                </span>
              </div>
            )}
            {(subscription.plan_detail.modules_included || []).map((modKey) => (
              <div
                key={modKey}
                className="flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-200 rounded-lg"
              >
                <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0" />
                <span className="text-sm font-medium text-green-800">
                  {MODULE_NAMES[modKey] || modKey}
                </span>
              </div>
            ))}
            {addonsToShow.map((addon) => (
              <div
                key={addon.module_key}
                className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg"
              >
                <CheckCircle2 className="h-4 w-4 text-blue-600 flex-shrink-0" />
                <span className="text-sm font-medium text-blue-800">
                  {MODULE_NAMES[addon.module_key] || addon.module_key} (Add-on)
                </span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Manage Billing Link */}
      {canManageOrganization && subscription.stripe_customer_id && (
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium text-gray-900">Billing & Invoices</h3>
                <p className="text-sm text-gray-600">
                  View invoices, update payment method, or cancel your subscription via the Stripe billing portal.
                </p>
              </div>
              <Button variant="outline" onClick={handleManageBilling} disabled={portalSession.isPending}>
                <ExternalLink className="h-4 w-4 mr-2" />
                Open Billing Portal
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
